if (!customElements.get("sticky-atc")) {
  customElements.define(
    "sticky-atc",
    class StickyAtc extends HTMLElement {
      constructor() {
        super();
        this.selectors = {
          prodTitle: ".m-sticky-addtocart--title",
          mainImage: ".m-sticky-addtocart--image",
          addToCart: ".m-add-to-cart",
          buyNowBtn: ".m-product-dynamic-checkout",
          variantIdSelect: '[name="id"]',
          foxkitBtn: ".foxkit-button",
          select: "select",
          variantSummary: ".m-sticky-addtocart--selection",
        };
        this.hasCustomFields = !!document.querySelector(".m-main-product--info .m-product-custom-field");
      }

      connectedCallback() {
        if (this.lifecycleController) return;
        this.mainProduct = document.querySelector("product-info[id^='MainProduct']");
        this.container = this.closest(".m-sticky-addtocart");
        this.mainProductForm = document.querySelector(".m-product-form--main");
        if (!this.mainProduct || !this.container || !this.mainProductForm) return;
        this.lifecycleController = new AbortController();
        this.mainProductInfo = document.querySelector(".m-main-product--info");
        this.mainAddToCart = this.mainProductForm ? this.mainProductForm.querySelector(".m-add-to-cart") : null;
        this.mainDynamicCheckout = this.mainProductForm.querySelector(this.selectors.buyNowBtn);
        this.disableSelectedVariantDefault = this.mainProduct.dataset.disableSelectedVariantDefault === "true" || false;
        this.domNodes = queryDomNodes(this.selectors, this.container);

        this.init();
      }

      disconnectedCallback() {
        this.lifecycleController?.abort();
        this.lifecycleController = null;
        this.observer?.disconnect();
        this.resizeObserver?.disconnect();
        this.mobileMediaQuery?.removeEventListener("change", this.onStickyLayoutChange);
        this.variantChangeUnsubscriber?.();
        this.variantChangeUnsubscriber = null;
        cancelAnimationFrame(this.layoutRequest);
        this.layoutRequest = null;
        if (!document.querySelector("sticky-atc")) {
          document.documentElement.style.removeProperty("--f-sticky-atc-bar-height");
          document.documentElement.classList.remove("stick-atc-show");
          window.MinimogSettings.stickyAddToCartHeight = 0;
        }
      }

      init() {
        this.initStickyReadability();
        if (!this.mainAddToCart) {
          this.container.style.setProperty("--m-translate-y", 0);
          return;
        }

        this.variantData = this.getVariantData();

        const headerHeight = MinimogSettings.headerHeight || 66;
        const rootMargin = `${headerHeight}px 0px 0px 0px`;

        if ("IntersectionObserver" in window) {
          this.observer = new IntersectionObserver(
            (entries) => {
              entries.forEach((entry) => {
                if (entry.intersectionRatio !== 1) {
                  this.container.style.setProperty("--m-translate-y", 0);
                } else {
                  this.container.style.setProperty("--m-translate-y", "100%");
                }
                document.documentElement.classList[entry.intersectionRatio !== 1 ? "add" : "remove"]("stick-atc-show");
              });
            },
            { threshold: 1, rootMargin }
          );
        }

        if (this.disableSelectedVariantDefault) {
          this.handleDisableSelectedVariantDefault();
        }

        const { prodTitle, mainImage } = this.domNodes;
        prodTitle.addEventListener("click", () => __scrollToTop(), { signal: this.lifecycleController.signal });
        mainImage.addEventListener("click", () => __scrollToTop(), { signal: this.lifecycleController.signal });

        this.handleCustomFields();

        this.setObserveTarget();
        this.syncWithMainProductForm();

        this.domNodes.select.addEventListener("change", (e) => {
          const { target } = e;
          const variantPicker = this.mainProduct.querySelector('variant-picker');
          const selectedVariantId = this.querySelector(this.selectors.variantIdSelect).value;
          this.currentVariant = this.variantData.find((variant) => variant.id === Number(selectedVariantId));
          this.currentVariant ? this.toggleAddButton(!this.currentVariant.available, window.MinimogStrings.soldOut) : this.toggleAddButton(true, window.MinimogStrings.unavailable);

          const selectedOption = target.options[target.selectedIndex];
          this.updateVariantSummary();
          const selectedOptionIds = selectedOption.dataset.options.split(',').filter(id => id);

          if (variantPicker) {
            selectedOptionIds.forEach((optionId) => {
              const input = variantPicker.querySelector(`[data-option-value-id="${optionId}"]`);
              const { tagName } = input;
              switch (tagName) {
                case "OPTION":
                  const inputParent = input.parentNode; // select tag
                  inputParent.value = input.value;
                  break;
                case "INPUT":
                  input.checked = true;
                  break;
              }
            });
            variantPicker.dispatchEvent(new Event("change"));
          }
        }, { signal: this.lifecycleController.signal });
      }

      initStickyReadability() {
        this.onStickyLayoutChange = () => this.scheduleStickyLayout();
        this.mobileMediaQuery = window.matchMedia(MinimogTheme.config.mediaQueryMobile);
        this.mobileMediaQuery.addEventListener("change", this.onStickyLayoutChange);
        window.addEventListener("resize", this.onStickyLayoutChange, { signal: this.lifecycleController.signal });
        this.container.addEventListener("transitionend", (event) => {
          if (event.target === this.container && event.propertyName === "transform") this.scheduleStickyLayout();
        }, { signal: this.lifecycleController.signal });
        if ("ResizeObserver" in window) {
          this.resizeObserver = new ResizeObserver(this.onStickyLayoutChange);
          this.resizeObserver.observe(this.container);
        }
        this.updateVariantSummary();
        this.scheduleStickyLayout();
      }

      updateVariantSummary() {
        const { variantSummary, select } = this.domNodes;
        if (!variantSummary) return;
        const selectedOption = select?.options[select.selectedIndex];
        variantSummary.textContent = selectedOption ? selectedOption.textContent.trim() : "";
        this.scheduleStickyLayout();
      }

      scheduleStickyLayout() {
        if (!this.lifecycleController || this.layoutRequest) return;
        this.layoutRequest = requestAnimationFrame(() => {
          this.layoutRequest = null;
          if (!this.isConnected || !this.lifecycleController) return;
          this.setStickyAddToCartHeight();
          this.keepFocusedOptionVisible();
        });
      }

      keepFocusedOptionVisible() {
        if (!this.mobileMediaQuery.matches || document.visibilityState !== "visible" || !document.hasFocus()) return;
        const active = document.activeElement;
        if (!active?.matches('input.m-product-option__input[type="radio"]:focus-visible, select:focus-visible')) return;
        if (!this.mainProduct.contains(active) || !active.closest("variant-picker")) return;
        const root = document.documentElement;
        if (root.classList.contains("prevent-scroll")) return;
        if ([root, document.body].some((node) => ["hidden", "clip"].includes(getComputedStyle(node).overflowY))) return;
        const modalOpen = Array.from(document.querySelectorAll('dialog[open], [aria-modal="true"], .m-cart-drawer--active')).some((node) => {
          const rect = node.getBoundingClientRect();
          const style = getComputedStyle(node);
          return rect.width > 0 && rect.height > 0 && rect.bottom > 0 && rect.top < window.innerHeight && style.visibility !== "hidden" && style.display !== "none";
        });
        if (modalOpen) return;
        const bar = this.container.getBoundingClientRect();
        const style = getComputedStyle(this.container);
        if (style.display === "none" || style.visibility === "hidden" || bar.height <= 0 || bar.top >= window.innerHeight || bar.bottom <= 0) return;
        let target = active;
        if (active.tagName === "INPUT") {
          const label = active.nextElementSibling;
          if (label?.tagName !== "LABEL" || label.htmlFor !== active.id) return;
          target = active.closest(".m-product-option--node") || label;
        }
        const rect = target.getBoundingClientRect();
        if (rect.height <= 0 || rect.top >= window.innerHeight || rect.bottom <= 0) return;
        const overlap = rect.bottom + 6 - bar.top;
        const headerHeight = Number(MinimogSettings.headerHeight) || 0;
        const availableScroll = Math.max(0, rect.top - headerHeight - 6);
        const delta = Math.min(overlap, availableScroll);
        if (delta > 0) window.scrollBy({ top: Math.ceil(delta), left: 0, behavior: "instant" });
      }

      getVariantData() {
        this.variantData = this.variantData || JSON.parse(this.container.querySelector('[type="application/json"]').textContent);
        return this.variantData;
      }

      setObserveTarget() {
        if (this.observer) {
          this.observer.observe(this.mainProductForm);
          this.observeTarget = this.mainProductForm;
        }
      }

      setUnavailable() {
        const productForm = this.querySelector('.m-product-form');
        const addButton = productForm.querySelector('[name="add"]');

        if (addButton) {
          this.toggleAddButton(true, window.MinimogStrings.unavailable);
        }
      }

      toggleAddButton(disable = true, text, modifyClass = true) {
        const productForm = this.querySelector('.m-product-form');
        if (!productForm) return;
        const addButton = productForm.querySelector('[name="add"]');
        const addButtonText = productForm.querySelector('[name="add"] > span.m-add-to-cart--text');
        if (!addButton) return;

        if (disable) {
          addButton.setAttribute('disabled', 'disabled');
          if (text) addButtonText.textContent = text;
        } else {
          addButton.removeAttribute('disabled');
          addButtonText.innerHTML = window.MinimogStrings.addToCart;
        }

        if (!modifyClass) return;
      }

      handleDisableSelectedVariantDefault() {
        let urlParams = new URLSearchParams(window.location.search);
        if (urlParams.has("variant")) return;

        const pickerFields = this.querySelector('.m-product-option--dropdown-select');

        pickerFields.value = '';
        this.updateVariantSummary();
        this.setUnavailable();
      }

      setStickyAddToCartHeight() {
        const height = Math.ceil(this.container.getBoundingClientRect().height);
        document.documentElement.style.setProperty("--f-sticky-atc-bar-height", height + "px");
        window.MinimogSettings.stickyAddToCartHeight = height;
      }

      syncWithMainProductForm() {
        const variantInput = this.querySelector('[name="id"]');
        this.variantChangeUnsubscriber = MinimogEvents.subscribe(MinimogTheme.pubSubEvents.variantChange, (e) => {
          const isMainProduct = e.data.sectionId === this.mainProduct.dataset.sectionId;
          if (!isMainProduct) return;
          this.currentVariant = e.data.variant;
          if (this.currentVariant) {
            variantInput.value = e.data.variant.id;
            this.toggleAddButton(!this.currentVariant.available, window.MinimogStrings.soldOut)
          } else {
            variantInput.value = '';
            this.toggleAddButton(true, window.MinimogStrings.unavailable)
          }
          this.updateVariantSummary();
        });
      }

      handleCustomFields() {
        if (!this.hasCustomFields) return;

        const { addToCart, buyNowBtn, foxkitBtn } = this.domNodes;

        let hasCustomFieldRequired = false;
        const customFields = document.querySelectorAll(".m-main-product--info .m-product-custom-field");
        let customFieldFirst = customFields[0];
        customFields &&
          customFields.forEach((item) => {
            const field = item.querySelector(".form-field");
            if (field.value == "" && field.hasAttribute("required")) {
              hasCustomFieldRequired = true;
            }
          });

        hasCustomFieldRequired &&
          addToCart.addEventListener("click", (e) => {
            e.preventDefault();
            e.stopPropagation();
            __scrollToTop(this.mainProductInfo, () => this.mainAddToCart.click());
          }, { signal: this.lifecycleController.signal });
        if (buyNowBtn) {
          buyNowBtn.addEventListener(
            "click",
            (e) => {
              const missing = validateForm(this.mainProductInfo);
              if (missing.length > 0) {
                e.preventDefault();
                e.stopPropagation();
                __scrollToTop(this.mainProductInfo, () => this.mainDynamicCheckout.click());
              }
            },
            { capture: true, signal: this.lifecycleController.signal }
          );
        }
        if (foxkitBtn) {
          foxkitBtn.addEventListener("click", (e) => {
            e.preventDefault();
            __scrollToTop(this.mainProductInfo, () => this.mainAddToCart.click());
          }, { signal: this.lifecycleController.signal });
        }
      }
    }
  );
}
